/* eslint-disable react/react-in-jsx-scope */
import {
	Abc,
	Add,
	AdminPanelSettings,
	Assignment,
	AssignmentInd,
	Build,
	Create,
	Explore,
	FeedbackOutlined,
	FileUpload,
	Flare,
	GraphicEqOutlined,
	Group,
	InfoOutlined,
	LocalPolice,
	ManageAccounts,
	PhoneAndroid,
	QueryStats,
	QuestionMark,
	VideoCameraBack,
	WarningAmber,
} from '@mui/icons-material';
import { PrometheusTab, SystemRoles } from '../../types/generic';
import ANPRTab from './Upload/ANPR';
import CellebriteTab from './Upload/Cellebrite';
import ErrorDisplay from '../ErrorDisplay';
import CreateOperation from './Management/CreateOperation';
import ManageOperations from './Management/ManageOperations';
import CreateKeyphrases from './Management/CreateKeyphrases';
import ManageKeyphrases from './Management/ManageKeyphrases';
import FAQs from './Help/FAQs';
import ManageOrgRoles from './Management/ManageOrgRoles';
import StellarCellebriteTab from './Upload/StellarCellebrite';
import Feedback from './Help/Feedback';
import UserViewTab from './Admin/Users';
import MetricsViewTab from './Admin/Metrics';
import LEAsTab from './Admin/LEAs';
import MaintenanceTab from './Admin/Maintenance';
import AxiomTab from './Upload/AXIOM';
import AdminDashboard from './Admin/Dashboard';

export const prometheusTabs: PrometheusTab[] = [
	{
		name: 'Upload',
		icon: FileUpload,
		helpText: 'Upload data to the Prometheus System',
		defaultChild: 1,
		children: [
			{
				name: 'ANPR',
				icon: VideoCameraBack,
				component: <ANPRTab />,
				helpText: 'Upload and analyse / attribute Automatic Number Plate Recognition data',
				requiredRoles: ['UPLOAD'],
			},
			{
				name: 'Cellebrite',
				icon: PhoneAndroid,
				component: <CellebriteTab />,
				helpText: 'Upload data extracted from suspect phones by Cellebrite for analysis',
				requiredRoles: ['UPLOAD'],
			},
			{
				name: 'Stellar',
				icon: Flare,
				component: <StellarCellebriteTab />,
				helpText: 'Upload data extracted from suspect phones for analysis by the STELLAR processing pipelines',
				requiredRoles: ['UPLOAD', 'UPLOAD_STELLAR'],
			},
			{
				name: 'AXIOM',
				icon: Explore,
				component: <AxiomTab />,
				helpText: 'Upload data extracted from suspect phones by AXIOM for analysis',
				requiredRoles: ['UPLOAD', 'UPLOAD_AXIOM'],
			},
		],
	},
	{
		name: 'Management',
		icon: ManageAccounts,
		helpText: 'Manage admin features of the Prometheus System',
		children: [
			{
				name: 'Create Operation',
				icon: Add,
				component: <CreateOperation />,
				helpText: 'Create Operations in the system',
				requiredRoles: ['CREATE_OP'],
			},
			{
				name: 'Manage Operations',
				icon: Assignment,
				component: <ManageOperations />,
				helpText: 'Assign, rename, and delete Operations from the system',
				requiredRoles: ['MANAGE_OP'],
			},
			{
				name: 'Create Keyphrases',
				icon: Create,
				component: <CreateKeyphrases />,
				helpText: 'Create or Update Keyphrases libraries',
				requiredRoles: ['DEVELOPER'],
			},
			{
				name: 'Manage Keyphrases',
				icon: Abc,
				component: <ManageKeyphrases />,
				helpText: 'View or Delete Keyphrases libraries',
				requiredRoles: ['DEVELOPER'],
			},
			{
				name: 'Manage Roles',
				icon: AssignmentInd,
				component: <ManageOrgRoles />,
				helpText: 'Assign and remove organisation roles',
				requiredRoles: ['MANAGE_ORG'],
			},
		],
	},
	{
		name: 'Admin',
		icon: AdminPanelSettings,
		helpText: 'View statistic and data about the users and usage of Prometheus',
		children: [
			{
				name: 'Dashboard',
				icon: GraphicEqOutlined,
				component: <AdminDashboard />,
				helpText: 'Admin Dashboard',
				requiredRoles: ['ADMINISTRATOR'],
			},
			{
				name: 'Metrics',
				icon: QueryStats,
				component: <MetricsViewTab />,
				helpText: 'View current Prometheus usage metrics',
				requiredRoles: ['ADMINISTRATOR'],
			},
			{
				name: 'LEAs',
				icon: LocalPolice,
				component: <LEAsTab />,
				helpText: 'View / update data relating to onboarded Prometheus forces',
				requiredRoles: ['ADMINISTRATOR'],
			},
			{
				name: 'Users',
				icon: Group,
				component: <UserViewTab />,
				helpText: 'View current Prometheus users',
				requiredRoles: ['ADMINISTRATOR'],
			},
			{
				name: 'System',
				icon: Build,
				component: <MaintenanceTab />,
				helpText: 'Settings for Maintenance Mode and Update Notice Banner',
				requiredRoles: ['ADMINISTRATOR'],
			},
		],
	},
	{
		name: 'Help',
		icon: InfoOutlined,
		helpText: 'Get Answers to FAQs and send feedback to the Prometheus team',
		children: [
			{
				name: 'FAQs',
				icon: QuestionMark,
				component: <FAQs />,
				helpText: 'Get Answers to Frequently Asked Questions',
			},
			{
				name: 'Feedback',
				icon: FeedbackOutlined,
				component: <Feedback />,
				helpText: 'Send a message or feature request to the Prometheus support team',
			},
		],
	},
];

export const fallbackTab: PrometheusTab = {
	name: 'Error',
	icon: WarningAmber,
	helpText: 'Permission Denied',
	component: (
		<ErrorDisplay
			errorText={[
				'You do not have any cases assigned to you, or permission to create / manage cases',
				'If these permissions have been assigned to you since logging in, please refresh the page',
			]}
			summaryText={`You do not have the required permissions to view this page`}
		/>
	),
};

const getTabChildPaths = (tab: PrometheusTab, curPath: string, userRoles: SystemRoles[]): string[] => {
	let allowedTabs: string[] = [];

	if (!tab.requiredRoles || userRoles.includes('SUPERUSER') || tab.requiredRoles.every((rr) => userRoles.includes(rr))) {
		const tabPath = curPath ? `${curPath}.${tab.name}` : tab.name;
		if (tab.children) {
			let childrenToAdd: string[] = [];
			tab.children
				.filter((child) => {
					if (userRoles.includes('SUPERUSER')) {
						return true;
					} else if (child.requiredRoles?.every((rr) => userRoles.includes(rr)) || !child.requiredRoles) {
						return true;
					} else {
						return false;
					}
				})
				.forEach((childTab) => {
					childrenToAdd = [...childrenToAdd, ...getTabChildPaths(childTab, tabPath, userRoles)];
				});
			if (childrenToAdd.length > 0) {
				allowedTabs = [...allowedTabs, tabPath, ...childrenToAdd];
			}
		} else {
			allowedTabs.push(tabPath);
		}
	}

	return allowedTabs;
};

/**
 * Generate an array of strings containing paths to all tabs allowed by the supplied user roles, effectively serialising them (allowing for redux storage)
 * @param userRoles list of roles that the user has in the system
 * @returns Array of tab paths
 */
export const getAllowedTabs = (userRoles: SystemRoles[]): string[] => {
	let allowedTabs: string[] = [];
	prometheusTabs.forEach((tab) => {
		allowedTabs = [...allowedTabs, ...getTabChildPaths(tab, '', userRoles)];
	});

	return allowedTabs;
};

const findMatchingTab = (curStep: string, steps: string[], tabs: PrometheusTab[]): PrometheusTab => {
	if (curStep) {
		const foundTab = tabs.find((tab) => tab.name === curStep);

		if (foundTab && foundTab.children && steps.length > 0) {
			return findMatchingTab(steps.splice(0, 1)[0], steps, foundTab.children);
		} else if (!foundTab) {
			return fallbackTab;
		} else {
			return foundTab;
		}
	} else {
		return fallbackTab;
	}
};

export const pathToTab = (path: string): PrometheusTab => {
	const pathSteps: string[] = path.split('.');

	if (pathSteps.length > 0) {
		const firstStep = pathSteps.splice(0, 1)[0];
		return findMatchingTab(firstStep, pathSteps, prometheusTabs);
	} else {
		return fallbackTab;
	}
};

export const getDefaultTab = (userRoles: SystemRoles[]): string => {
	if (userRoles.includes('SUPERUSER') || userRoles.includes('UPLOAD')) {
		return 'Upload.Cellebrite';
	} else if (userRoles.includes('VIEW')) {
		return 'Data.Chat Viewer';
	} else if (userRoles.includes('CREATE_OP')) {
		return 'Management.Create Operation';
	} else if (userRoles.includes('MANAGE_OP')) {
		return 'Management.Manage Operations';
	} else if (userRoles.includes('ADMINISTRATOR')) {
		return 'Admin.Metrics';
	} else {
		return 'Error';
	}
};
